import { Match } from '@libs/src/models/match.interfaces';
import { Program } from '@libs/src/models/program.model';
import { SurveyData } from '@main-client/src/app/program-survey-result-drawer/program-survey-result-drawer';

import compact from 'lodash-es/compact';
import flatMap from 'lodash-es/flatMap';
import includes from 'lodash-es/includes';
import mean from 'lodash-es/mean';
import uniq from 'lodash-es/uniq';

const scores = {
  expectedDropOffPerSession: 0.05,
  scoreForBenchmark: 0.6,
  scoreForMessagedMatch: 1,
  scoreForSeenMatch: 0.5,
  scoreForSurveyResponse: 1,
  threshold1ForBenchmark: 0.9,
  threshold2ForBenchmark: 1 - 0.9,
};

export function calculateProgramHealth(
  activeMatchedAccountIds: string[],
  matchSeenAccountIds: string[],
  matchMessagedAccountIds: string[],
  surveys: SurveyData[] = [],
) {
  const participantsCount = activeMatchedAccountIds.length;
  const matchStatusScore =
    matchSeenAccountIds.length * scores.scoreForSeenMatch +
    matchMessagedAccountIds.length * scores.scoreForMessagedMatch;
  const matchStatusWeightedScore = getWeightedScore(
    matchStatusScore / participantsCount,
  );
  const surveyResponseWeightedScores = surveys.map(
    ({ surveyResponses }, index) =>
      getWeightedScore(
        surveyResponses.length / participantsCount,
        scores.scoreForBenchmark *
          (1 - scores.expectedDropOffPerSession * index),
      ),
  );
  return mean([matchStatusWeightedScore, ...surveyResponseWeightedScores]);
}

function getWeightedScore(
  average: number,
  benchmark: number = scores.scoreForBenchmark,
  weight1: number = scores.threshold1ForBenchmark,
  weight2: number = scores.threshold2ForBenchmark,
) {
  const weightedScore1 = Math.min(average / benchmark, 1) * weight1;
  const weightedScore2 = Math.max(
    ((average - benchmark) / (1 - benchmark)) * weight2,
    0,
  );
  return weightedScore1 + weightedScore2 || 0;
}

export function getProgramHealth(surveys: SurveyData[] = [], matches: Match[]) {
  const flattenedMatchAccountDocuments = flatMap(
    matches,
    ({ accounts }) => accounts,
  );
  const matchAccountsWithSeenStatus = flattenedMatchAccountDocuments
    .filter(({ status }) => 'seen' === status)
    .map(({ document }) => document?._id);
  const matchAccountsWithMessagedStatus = flattenedMatchAccountDocuments
    .filter(({ status }) => 'active' === status)
    .map(({ document }) => document?._id);
  const filteredSurveys = surveys.map((survey) => ({
    ...survey,
    surveyResponses: survey.surveyResponses.filter((surveyResponse) =>
      includes(
        flattenedMatchAccountDocuments.map(({ document }) => document?._id),
        surveyResponse.account?._id,
      ),
    ),
  }));
  const accountsWhoSubmittedSurveyResponse = uniq(
    flatMap(filteredSurveys, (survey) =>
      survey.surveyResponses.map(({ account }) => account._id),
    ),
  );
  const programHealth = calculateProgramHealth(
    flattenedMatchAccountDocuments.map(({ document }) => document?._id),
    matchAccountsWithSeenStatus,
    matchAccountsWithMessagedStatus,
    filteredSurveys,
  );
  return {
    healthScore: programHealth,
    totalAccountWhoEngaged: uniq(
      compact([
        ...matchAccountsWithMessagedStatus,
        ...accountsWhoSubmittedSurveyResponse,
      ]),
    ).length,
    totalMatchedAccounts: flattenedMatchAccountDocuments.length,
  };
}

export function getProgramProgress(program: Program) {
  if ('draft' === program.status) {
    const setupChecklist = program.setup_checklist;
    const steps = Object.keys(setupChecklist);
    const numberOfSteps = steps.length;
    const completedStepsRate =
      steps.filter((step) => setupChecklist[step]).length / numberOfSteps;
    return {
      completionRate: Math.round(completedStepsRate * 100),
      steps: numberOfSteps,
    };
  }
  const phasesLength = program.phases.length;
  const completedPhasesRate =
    program.phases.filter((phase) => phase.phase_completed).length /
    phasesLength;
  return {
    completionRate: Math.round(completedPhasesRate * 100),
    steps: phasesLength,
  };
}
